/**
 * A reading link's cryptography on the device (decision 58; principle 9; part 38d log, principles DP1–DP4): the fragment
 * key K and the owner secret S, made at random and kept only in the share-link record; the owner proof and the link's ID
 * derived from S, which are all the relay is sent of it; the snapshot's parts and the thread items sealed under keys
 * HKDF derives from K, each authenticated with what it belongs to, so the relay can't reorder, truncate, relabel or move
 * one between links. The reader page (part 39) opens what this seals with the same functions, and proves its thread by a
 * proof derived from the thread secret its browser keeps (part 39 log, RP1). WebCrypto is injected.
 */

/** The fragment key's length: AES-256 (DP1). */
export const SHARE_KEY_BYTES = 32;

/** The owner secret's length: decision 58's 128 random bits (DP1). */
export const OWNER_SECRET_BYTES = 16;

/** The owner proof's length: a SHA-256 (part 38's `LINK_PROOF_BYTES`). */
export const OWNER_PROOF_BYTES = 32;

/** A link's ID: 16 bytes (part 38's `LINK_ID_BYTES`). */
export const LINK_ID_BYTES = 16;

/** An item's ID: 16 bytes (part 38's `ITEM_ID_BYTES`). */
export const ITEM_ID_BYTES = 16;

/** The bytes of every snapshot part but the last (part 38's `SNAPSHOT_PART_BYTES`). */
export const PART_BYTES = 2 * 1024 * 1024;

/** The most parts a snapshot has (part 38's `SNAPSHOT_MAX_PARTS`). */
export const MAX_PARTS = 30;

/** AES-GCM's tag, which every sealed part and item carries. */
export const TAG_BYTES = 16;

/** AES-GCM's IV. */
export const IV_BYTES = 12;

/** The plaintext a whole part holds: its bytes less the tag (a part's IV is its index, never sent). */
export const PART_PLAIN_BYTES = PART_BYTES - TAG_BYTES;

/** The most plaintext a snapshot holds: {@link MAX_PARTS} whole parts. */
export const MAX_SNAPSHOT_PLAIN_BYTES = MAX_PARTS * PART_PLAIN_BYTES;

/** Fix-next 84, DP9: the writer's tag after a writer's item's sealed bytes: an HMAC-SHA-256. */
export const WRITER_ITEM_TAG_BYTES = 32;

/** A reader's thread secret: 256 random bits, kept in the reader's browser (part 39 log, RP1). */
export const THREAD_SECRET_BYTES = 32;

/** Who wrote an item, as the relay labels it (part 38's `author`). */
export type ItemAuthor = 'reader' | 'writer';

const encoder = new TextEncoder();
const OWNER_DOMAIN = encoder.encode('cockatrice/share-link/owner/v1');
const THREAD_DOMAIN = encoder.encode('cockatrice/share-link/thread/v1');
const ID_DOMAIN = encoder.encode('cockatrice/share-link/id/v1');
const NOTE_DOMAIN = encoder.encode('cockatrice/share-link/note/v1');
const PART_DOMAIN = encoder.encode('cockatrice/share-link/part/v1');
const ITEM_DOMAIN = encoder.encode('cockatrice/share-link/item/v1');
const WRITER_TAG_DOMAIN = encoder.encode('cockatrice/share-link/writer-tag/v1');
const PART_KEY_LABEL = encoder.encode('cockatrice/share-link/part-key/v1');
const ITEM_KEY_LABEL = encoder.encode('cockatrice/share-link/item-key/v1');

/** `parts` joined, in order. */
function joined(...parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** `value` as four bytes, big-endian. */
function u32(value: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, value);
  return out;
}

/** SHA-256 of `bytes`. */
async function sha256(subtle: SubtleCrypto, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle.digest('SHA-256', bytes));
}

/** Throws unless `bytes` has `length` bytes: a caller's mistake, never input from outside (that is checked at its boundary). */
function lengthOf(bytes: Uint8Array, length: number, what: string): void {
  if (bytes.length !== length) throw new RangeError(`${what} must be ${String(length)} bytes`);
}

/** DP2: the owner proof of owner secret `secret`: SHA-256 of its domain and the secret. */
export async function ownerProof(subtle: SubtleCrypto, secret: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, OWNER_SECRET_BYTES, 'an owner secret');
  return sha256(subtle, joined(OWNER_DOMAIN, secret));
}

/**
 * Part 39, RP1 (SL11): the thread proof of thread secret `secret`: SHA-256 of its domain and the secret, which is all the
 * relay is sent of it; the relay keeps the proof's own hash.
 */
export async function threadProof(subtle: SubtleCrypto, secret: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, THREAD_SECRET_BYTES, 'a thread secret');
  return sha256(subtle, joined(THREAD_DOMAIN, secret));
}

/** DP2, part 38's LA24: the only ID a link of owner proof `proof` may have: the first 16 bytes of SHA-256(domain ‖ proof). */
export async function linkIdOf(subtle: SubtleCrypto, proof: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(proof, OWNER_PROOF_BYTES, 'an owner proof');
  return (await sha256(subtle, joined(ID_DOMAIN, proof))).slice(0, LINK_ID_BYTES);
}

/**
 * DP5, C6: the ID of the note an item of link `link` becomes: the first 16 bytes of SHA-256(domain ‖ link ‖ item), so the
 * same item is the same note on every device and pull, and no reader can pick an ID a note of the writer's holds.
 */
export async function noteIdOf(subtle: SubtleCrypto, link: Uint8Array, item: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(link, LINK_ID_BYTES, 'a link ID');
  lengthOf(item, ITEM_ID_BYTES, 'an item ID');
  return (await sha256(subtle, joined(NOTE_DOMAIN, link, item))).slice(0, ITEM_ID_BYTES);
}

/** The keys HKDF derives from a link's fragment key: one for the snapshot's parts, one for the thread items (DP4). */
export interface ShareKeys {
  readonly parts: CryptoKey;
  readonly items: CryptoKey;
}

/** The keys of fragment key `raw`, neither extractable. */
export async function shareKeysOf(subtle: SubtleCrypto, raw: Uint8Array<ArrayBuffer>): Promise<ShareKeys> {
  lengthOf(raw, SHARE_KEY_BYTES, 'a fragment key');
  const base = await subtle.importKey('raw', raw, 'HKDF', false, ['deriveKey']);
  const derive = (info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> =>
    subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  return { parts: await derive(PART_KEY_LABEL), items: await derive(ITEM_KEY_LABEL) };
}

/** How many parts a snapshot of `plain` bytes of plaintext goes up in; null for none or past {@link MAX_SNAPSHOT_PLAIN_BYTES}. */
export function partsFor(plain: number): number | null {
  if (!Number.isSafeInteger(plain) || plain < 1 || plain > MAX_SNAPSHOT_PLAIN_BYTES) return null;
  return Math.ceil(plain / PART_PLAIN_BYTES);
}

/** The size the relay is told of a snapshot of `plain` bytes in `parts` parts: the plaintext and a tag per part. */
export function sealedSize(plain: number, parts: number): number {
  return plain + parts * TAG_BYTES;
}

/** A part's IV: its index, so a retried part seals the same plaintext to the same bytes (the key is the link's alone). */
function partIv(index: number): Uint8Array<ArrayBuffer> {
  return joined(new Uint8Array(IV_BYTES - 4), u32(index));
}

/** DP4: what a part is authenticated with: its domain, the link, its index and the part count. */
function partData(link: Uint8Array, index: number, parts: number): Uint8Array<ArrayBuffer> {
  return joined(PART_DOMAIN, link, u32(index), u32(parts));
}

/**
 * DP4: the snapshot `plain` of link `link`, sealed in parts: every part but the last {@link PART_BYTES} long, as the
 * relay's part lengths are (part 38's `partLength`). Throws `RangeError` for an empty or oversized snapshot.
 */
export async function sealParts(subtle: SubtleCrypto, keys: ShareKeys, link: Uint8Array, plain: Uint8Array): Promise<Uint8Array<ArrayBuffer>[]> {
  lengthOf(link, LINK_ID_BYTES, 'a link ID');
  const parts = partsFor(plain.length);
  if (parts === null) throw new RangeError(`a snapshot must be 1 to ${String(MAX_SNAPSHOT_PLAIN_BYTES)} bytes`);
  const sealed: Uint8Array<ArrayBuffer>[] = [];
  for (let index = 0; index < parts; index += 1) {
    const chunk = plain.slice(index * PART_PLAIN_BYTES, (index + 1) * PART_PLAIN_BYTES);
    sealed.push(new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv: partIv(index), additionalData: partData(link, index, parts) }, keys.parts, chunk)));
  }
  return sealed;
}

/** Part `index` of `parts` of link `link`'s snapshot opened; null when it doesn't open (another link's, moved, altered). */
export async function openPart(subtle: SubtleCrypto, keys: ShareKeys, link: Uint8Array, index: number, parts: number, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  try {
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: partIv(index), additionalData: partData(link, index, parts) }, keys.parts, bytes));
  } catch {
    return null;
  }
}

/** DP4: what an item is authenticated with: its domain, the link, the item's ID and its author. */
function itemData(link: Uint8Array, item: Uint8Array, author: ItemAuthor): Uint8Array<ArrayBuffer> {
  return joined(ITEM_DOMAIN, link, item, Uint8Array.of(author === 'writer' ? 1 : 0));
}

/** DP4: item `item` of link `link` by `author`, its plaintext `plain` sealed with the fresh IV `iv`: the IV, then the ciphertext. */
export async function sealItem(
  subtle: SubtleCrypto,
  keys: ShareKeys,
  link: Uint8Array,
  item: Uint8Array,
  author: ItemAuthor,
  plain: Uint8Array<ArrayBuffer>,
  iv: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(link, LINK_ID_BYTES, 'a link ID');
  lengthOf(item, ITEM_ID_BYTES, 'an item ID');
  lengthOf(iv, IV_BYTES, 'an IV');
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: itemData(link, item, author) }, keys.items, plain)));
}

/** DP4, DP6: an item's bytes opened as item `item` of link `link` by `author`; null when they don't open. */
export async function openItem(subtle: SubtleCrypto, keys: ShareKeys, link: Uint8Array, item: Uint8Array, author: ItemAuthor, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  if (link.length !== LINK_ID_BYTES || item.length !== ITEM_ID_BYTES || bytes.length < IV_BYTES + TAG_BYTES) return null;
  try {
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES), additionalData: itemData(link, item, author) }, keys.items, bytes.slice(IV_BYTES)));
  } catch {
    return null;
  }
}

/** DP9: the HMAC key of a link's writer's tag, from its owner secret `secret` (never the owner proof the relay holds). */
async function writerTagKey(subtle: SubtleCrypto, secret: Uint8Array): Promise<CryptoKey> {
  const raw = await sha256(subtle, joined(WRITER_TAG_DOMAIN, secret));
  return subtle.importKey('raw', raw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

/**
 * Fix-next 84, DP9 (the security audit of 2026-11-02): a writer's item `item` of link `link`, `plain` sealed as
 * {@link sealItem} seals it as the writer's, then the writer's tag: an HMAC over the item's place and its sealed bytes under
 * a key only the owner secret `secret` gives. The fragment key every reader holds seals and opens an item labelled the
 * writer's as well as the writer does; the tag is what only the writer's devices make, and check ({@link openWriterItem}),
 * as invite notes' DN7.
 */
export async function sealWriterItem(
  subtle: SubtleCrypto,
  keys: ShareKeys,
  secret: Uint8Array,
  link: Uint8Array,
  item: Uint8Array,
  plain: Uint8Array<ArrayBuffer>,
  iv: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, OWNER_SECRET_BYTES, 'an owner secret');
  const sealed = await sealItem(subtle, keys, link, item, 'writer', plain, iv);
  const tag = new Uint8Array(await subtle.sign('HMAC', await writerTagKey(subtle, secret), joined(itemData(link, item, 'writer'), sealed)));
  return joined(sealed, tag);
}

/**
 * DP9: a writer's item's bytes opened on the writer's devices, holding owner secret `secret`: null unless its writer's tag
 * holds (one sealed by any other holder of the fragment key, a reader or the relay, never passes for the writer's).
 */
export async function openWriterItem(subtle: SubtleCrypto, keys: ShareKeys, secret: Uint8Array, link: Uint8Array, item: Uint8Array, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  if (secret.length !== OWNER_SECRET_BYTES || link.length !== LINK_ID_BYTES || item.length !== ITEM_ID_BYTES || bytes.length < IV_BYTES + TAG_BYTES + WRITER_ITEM_TAG_BYTES) return null;
  const sealed = bytes.slice(0, bytes.length - WRITER_ITEM_TAG_BYTES);
  const tag = bytes.slice(bytes.length - WRITER_ITEM_TAG_BYTES);
  if (!(await subtle.verify('HMAC', await writerTagKey(subtle, secret), tag, joined(itemData(link, item, 'writer'), sealed)))) return null;
  return openItem(subtle, keys, link, item, 'writer', sealed);
}

/**
 * DP9, on the reader page (which holds no owner secret, so checks no tag: its readers trust the relay to serve the
 * writer's items as the writer's, SL13): an item's bytes by `author` opened, a writer's item without its tag.
 */
export async function openItemAsReader(subtle: SubtleCrypto, keys: ShareKeys, link: Uint8Array, item: Uint8Array, author: ItemAuthor, bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer> | null> {
  if (author === 'reader') return openItem(subtle, keys, link, item, author, bytes);
  return bytes.length < WRITER_ITEM_TAG_BYTES ? null : openItem(subtle, keys, link, item, author, bytes.slice(0, bytes.length - WRITER_ITEM_TAG_BYTES));
}
